/* eslint-disable @typescript-eslint/no-explicit-any */
// VENDOR
import axios from 'axios';
import type {
  AxiosError,
  AxiosResponse,
  RawAxiosRequestConfig
} from 'axios';

// Stores
import { useApiStore } from '@stores/api';

// Session maanger
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

// PERSONAL LIBS
import { HttpMethodEnum } from '@shared/enum/httpmethod.enum';
import {
  addAbortController,
  buildUrl,
  defaultAxiosConfig,
  defaultRequestParams,
  hasFile,
  IRequestParams,
  isUploadEndpoint,
  manageError,
  onUploadProgressAxios,
  preparePostData,
  removeAbortController,
  testApiValidity
} from './api.lib';

// ********************************************************************************************************** HEAD REQUEST
const apiHead = async (
  api: string,
  params: IRequestParams = defaultRequestParams,
): Promise<AxiosResponse | undefined> => {
  // Check token and refresh
  if (!(await testApiValidity(params.publicApi))) {
    return;
  }

  // Prepare URL
  const url = await buildUrl(api, params);

  // Prepare cancellation action
  const controller = new AbortController();
  addAbortController(controller);

  return axios
    .head(
      url,
      defaultAxiosConfig({
        signal: controller.signal,
        withCredentials: params.withCredentials,
      }),
    )
    .then(async (response) => response)
    .catch((error: AxiosError) => {
      if (params?.bypassManageError !== true) {
        return manageError(HttpMethodEnum.HEAD, api, error);
      }
    })
    .finally(() => removeAbortController(controller));
};

// ********************************************************************************************************** GET REQUEST IN BLOB MODE
const apiGetBlob = async (
  api: string,
  params: IRequestParams = defaultRequestParams,
): Promise<any> => {
  // Check token and refresh
  if (!(await testApiValidity(params.publicApi))) {
    return;
  }

  // Prepare URL
  const url = await buildUrl(api, params);

  // Prepare cancellation action
  const controller = new AbortController();
  addAbortController(controller);

  return axios
    .get(
      url,
      defaultAxiosConfig({ signal: controller.signal, responseType: 'blob' }),
    )
    .then(async (response) => {
      if (response.data != null) {
        return response.data;
      } else {
        return response;
      }
    })
    .catch((error: AxiosError) => manageError(HttpMethodEnum.GET, api, error))
    .finally(() => removeAbortController(controller));
};

// ********************************************************************************************************** GET REQUEST
const apiGet = async (
  api: string,
  params: IRequestParams = defaultRequestParams,
): Promise<any> => {
  // Check token and refresh
  if (!(await testApiValidity(params.publicApi))) {
    return;
  }

  // Prepare URL
  const url = await buildUrl(api, params);

  // Prepare cancellation action
  const controller = new AbortController();
  addAbortController(controller);

  return axios
    .get(
      url,
      defaultAxiosConfig({
        signal: controller.signal,
        withCredentials: params.withCredentials,
      }),
    )
    .then((response) => response.data)
    .catch((error: AxiosError) => {
      if (params?.bypassManageError !== true) {
        return manageError(HttpMethodEnum.GET, api, error);
      }
    })
    .finally(() => removeAbortController(controller));
};

// ********************************************************************************************************** PUT REQUEST
const apiPut = async (api: string, putData?: any): Promise<any> => {
  // Check token and refresh
  if (!(await sessionManager.checkAccessTokenValidity())) {
    return;
  }

  // Prepare cancellation action
  const controller = new AbortController();
  addAbortController(controller);

  // Prepare URL
  const url = await buildUrl(api);

  return axios
    .put(url, putData, defaultAxiosConfig({ signal: controller.signal }))
    .then((response) => response.data)
    .catch((error: AxiosError) => manageError(HttpMethodEnum.PUT, api, error))
    .finally(() => removeAbortController(controller));
};

// ********************************************************************************************************** POST REQUEST
const apiPost = async (
  api: string,
  postData?: string | Blob | object,
  params?: IRequestParams,
): Promise<any> => {
  // Check token and refresh
  if (!(await testApiValidity(params?.publicApi))) {
    return;
  }

  // Prepare cancellation action
  const controller = params?.abortController ?? new AbortController();
  addAbortController(controller);

  // Prepare URL
  const url = await buildUrl(api);

  // Prepare POST Data
  const formData = postData != null ? preparePostData(postData) : undefined;

  // Prepare request param
  const axiosRequestConfig: RawAxiosRequestConfig = Object.assign(
    defaultAxiosConfig({ signal: controller.signal }),
    {
      method: HttpMethodEnum.POST,
      url: url,
      data: formData,
      onUploadProgress: isUploadEndpoint(api) && hasFile(postData) ? onUploadProgressAxios : undefined,
    },
  );

  return axios(axiosRequestConfig)
    .then((response) => response.data)
    .catch((error: AxiosError) => manageError(HttpMethodEnum.POST, api, error))
    .finally(() => removeAbortController(controller));
};

// ********************************************************************************************************** DELETE REQUEST
const apiDelete = async (api: string): Promise<any> => {
  // Check token and refresh
  if (!(await sessionManager.checkAccessTokenValidity())) {
    return;
  }

  // Prepare URL
  const url = await buildUrl(api);

  // Prepare cancellation action
  const controller = new AbortController();
  addAbortController(controller);

  // Prepare request param
  const axiosRequestConfig: RawAxiosRequestConfig = Object.assign(
    defaultAxiosConfig({ signal: controller.signal }),
    {
      method: HttpMethodEnum.DELETE,
      url: url,
    },
  );

  return axios(axiosRequestConfig)
    .then((response) => response.data)
    .catch((error: AxiosError) =>
      manageError(HttpMethodEnum.DELETE, api, error),
    )
    .finally(() => removeAbortController(controller));
};

// ********************************************************************************************************** PUBLIC METHODS
/**
 * Clear all cancel tokens
 */
export const clearApiRequests = () => {
  // Stores
  const apiStore = useApiStore();
  apiStore.clearApiRequests();
};


export {
  apiPut,
  apiPost,
  apiGet,
  apiGetBlob,
  apiHead,
  apiDelete
};
